// MAP SELECT (owner 2026-11-02: "change the map selection page to include both concept image of the map along with the name
// of it, include toggle or selector for day/night"). Title → START RUN → CHARACTER SELECT → MAP SELECT → RUN SETUP → run.
// A carousel of big map cards (concept image + name + tagline + best time); locked maps are darkened with a lock and their
// unlock requirement; unfinished maps say COMING SOON. Under it a Day / Night / Live cycle segmented control with the
// map's default marked. Remembers the last map and the last time choice per map (profile.maps, src/app/mapSave.ts).
// Controls: ←/→ (A/D, D-pad, stick) choose the map; ↓ moves to the time row and the buttons; LB/RB ([ ]) change the time
// from anywhere; A / Enter / Space continue; B / Esc back. Mouse / touch: tap a card, tap a time, tap CONTINUE.
// DOM only; no gameplay rules here (startRun hands map + time to the Sim).
import type { TodChoice } from '../core/mapdef';
import { conceptPath, TOD_CHOICES } from '../core/mapdef';
import { MAPS, mapById } from '../maps/registry';
import type { MapsSave } from './mapSave';
import { mapOpen, mapPlayable, todFor } from './mapSave';
import type { UiAction } from './input';
import type { EndlessBest } from './endlessSave';
import { endlessTierLabel } from '../core/endless';

export interface MapSelectHost {
  click(): void;
  deny(): void;
  show(s: 'charsel' | 'runsetup'): void;
  /** the profile's map section (mutated: selected map + time choices) */
  maps(): MapsSave;
  save(): void;
  /** ?mapsall: everything unlocked and playable, nothing saved */
  all: boolean;
  /** ENDLESS GOALS: the map's endless bests (null = never reached endless there) */
  endless?(id: string): EndlessBest | null;
}

const $ = <T extends HTMLElement>(s: string) => document.querySelector(s) as T;
const mmss = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
export const TOD_LABEL: Record<TodChoice, string> = { day: 'DAY', night: 'NIGHT', cycle: 'LIVE CYCLE' };
const TOD_ICON: Record<TodChoice, string> = { day: '☀', night: '☾', cycle: '◐' };
const LOCK_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M7 10V7a5 5 0 0 1 10 0v3h1.5A1.5 1.5 0 0 1 20 11.5v9A1.5 1.5 0 0 1 18.5 22h-13A1.5 1.5 0 0 1 4 20.5v-9A1.5 1.5 0 0 1 5.5 10H7Zm2 0h6V7a3 3 0 0 0-6 0v3Z"/></svg>';

export class MapSelect {
  /** focused card index (MAPS order) */
  sel = 0;
  /** 0 = cards, 1 = time row, 2 = buttons */
  row = 0;
  /** button column on row 2: 0 continue, 1 back */
  btn = 0;
  /** time choice per map shown on the page (seeded from the save) */
  private tod = new Map<string, TodChoice>();
  /** QA: build every card's image URL with a cache-buster (fresh concept stills) */
  bust = '';

  constructor(private host: MapSelectHost) {
    $('#ms-track').addEventListener('click', (e) => {
      const card = (e.target as HTMLElement).closest('.ms-card') as HTMLElement | null; if (!card) return;
      const i = Number(card.dataset.i); this.row = 0;
      if (i === this.sel) this.go(); else { this.sel = i; this.host.click(); this.remember(); this.render(); }
    });
    $('#ms-tod').addEventListener('click', (e) => {
      const b = (e.target as HTMLElement).closest('.ms-seg') as HTMLElement | null; if (!b) return;
      this.row = 1; this.setTod(b.dataset.t as TodChoice);
    });
    $('#ms-go').addEventListener('click', () => this.go());
    $('#ms-back').addEventListener('click', () => this.host.show('charsel'));
  }

  get map() { return MAPS[this.sel] ?? MAPS[0]; }
  todOf(id: string): TodChoice { return this.tod.get(id) ?? todFor(this.host.maps(), id); }
  playable(id: string): boolean { return mapPlayable(this.host.maps(), id, this.host.all); }
  /** what startRun uses */
  choice(): { map: string; tod: TodChoice } {
    const m = this.map, ok = this.playable(m.id);
    const id = ok ? m.id : mapById(this.host.maps().selected).id;
    return { map: id, tod: this.todOf(id) };
  }

  /** called by setScreen when the page opens */
  enter(): void {
    const ms = this.host.maps();
    const i = MAPS.findIndex((m) => m.id === ms.selected); this.sel = i >= 0 ? i : 0;
    if (!this.playable(this.map.id)) this.sel = Math.max(0, MAPS.findIndex((m) => this.playable(m.id)));
    this.row = 0; this.btn = 0;
    this.render();
  }

  private remember(): void {
    if (this.host.all || !this.playable(this.map.id)) return;
    const ms = this.host.maps(); ms.selected = this.map.id; this.host.save();
  }
  private setTod(t: TodChoice): void {
    const m = this.map;
    this.tod.set(m.id, t);
    if (!this.host.all && this.playable(m.id)) { const ms = this.host.maps(); ms.tod[m.id] = t; this.host.save(); }
    this.host.click(); this.render();
  }
  private stepTod(d: number): void {
    const cur = TOD_CHOICES.indexOf(this.todOf(this.map.id)), n = TOD_CHOICES.length;
    this.setTod(TOD_CHOICES[(cur + d + n) % n]);
  }
  private go(): void {
    if (!this.playable(this.map.id)) { this.host.deny(); this.flashLocked(); return; }
    this.remember(); this.host.click(); this.host.show('runsetup');
  }
  private flashLocked(): void { const c = document.querySelector('.ms-card.sel'); c?.classList.remove('nope'); void (c as HTMLElement | null)?.offsetWidth; c?.classList.add('nope'); }

  /** requirement text for a closed map */
  static lockText(id: string, ms: MapsSave): string {
    const def = mapById(id); if (!def.unlock.after) return '';
    const prev = mapById(def.unlock.after), need = def.unlock.seconds ?? 0, best = ms.best[prev.id] ?? 0;
    return `Survive ${mmss(need)} in ${prev.name} · best ${mmss(best)}`;
  }

  render(): void {
    const ms = this.host.maps();
    let html = '';
    MAPS.forEach((m, i) => {
      const open = this.host.all || mapOpen(ms, m.id), wip = m.status !== 'ready' && !this.host.all, ok = this.playable(m.id);
      const best = ms.best[m.id] ?? 0;
      const foot = !open ? MapSelect.lockText(m.id, ms) : wip ? 'In development' : best > 0 ? `Best ${mmss(best)}` : 'Not played yet';
      const eb = open && !wip ? this.host.endless?.(m.id) ?? null : null; // ENDLESS GOALS: best tier reached
      const cls = `ms-card${i === this.sel ? ' sel' : ''}${i === this.sel && this.row === 0 ? ' focus' : ''}${!open ? ' locked' : ''}${wip && open ? ' wip' : ''}`;
      html += `<button class="${cls}" data-i="${i}" aria-label="${m.name}${ok ? '' : ' (locked)'}">`
        + `<span class="ms-img" style="background-image:url('${conceptPath(m.id)}${this.bust}')"></span>`
        + (!open ? `<span class="ms-badge lock">${LOCK_SVG}<b>LOCKED</b></span>` : wip ? '<span class="ms-badge soon"><b>COMING SOON</b></span>' : '')
        + `<span class="ms-num">${m.order}</span>`
        + `<span class="ms-txt"><span class="ms-name">${m.name}</span><span class="ms-foot">${foot}</span>${eb ? `<span class="ms-end" title="Best endless: +${mmss(eb.time)} past the boss">✦ ${eb.tier ? endlessTierLabel(eb.tier) : 'ENDLESS'} · +${mmss(eb.time)}</span>` : ''}</span></button>`;
    });
    $('#ms-track').innerHTML = html;
    const m = this.map, open = this.host.all || mapOpen(ms, m.id), ok = this.playable(m.id);
    $('#ms-tag').textContent = open ? m.tagline : `Locked · ${MapSelect.lockText(m.id, ms)}`;
    const cur = this.todOf(m.id), def = m.time.default;
    $('#ms-tod').innerHTML = TOD_CHOICES.map((t) => `<button class="ms-seg${t === cur ? ' on' : ''}" data-t="${t}" aria-pressed="${t === cur}">`
      + `<span class="ms-ti">${TOD_ICON[t]}</span>${TOD_LABEL[t]}${t === def ? '<small>DEFAULT</small>' : ''}</button>`).join('');
    $('#ms-tod').classList.toggle('focus', this.row === 1);
    $('#ms-tod-desc').textContent = cur === 'day' ? `${({ dawn: 'Dawn', noon: 'Midday', dusk: 'Dusk' } as const)[m.time.day]} light for the whole run.`
      : cur === 'night' ? 'Moonlit night for the whole run. Foes press a little harder after dark.'
      : 'One full day and night over the run: dusk around the middle, a moonlit night, dawn near the end.';
    $<HTMLButtonElement>('#ms-go').disabled = !ok;
    $('#ms-go').classList.toggle('focus', this.row === 2 && this.btn === 0);
    $('#ms-back').classList.toggle('focus', this.row === 2 && this.btn === 1);
    const card = document.querySelector('#ms-track .ms-card.sel') as HTMLElement | null;
    card?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
  }

  handle(a: UiAction): void {
    const n = MAPS.length;
    if (a === 'back' || a === 'pause') { this.host.show('charsel'); return; }
    if (a === 'tabPrev' || a === 'tabNext') { this.stepTod(a === 'tabNext' ? 1 : -1); return; }
    if (a === 'left' || a === 'right') {
      const d = a === 'right' ? 1 : -1;
      if (this.row === 0) { const s = Math.max(0, Math.min(n - 1, this.sel + d)); if (s !== this.sel) { this.sel = s; this.remember(); this.host.click(); } }
      else if (this.row === 1) { this.stepTod(d); return; }
      else { this.btn = this.btn ? 0 : 1; this.host.click(); }
    } else if (a === 'down') { if (this.row < 2) { this.row++; this.btn = 0; this.host.click(); } }
    else if (a === 'up') { if (this.row > 0) { this.row--; this.host.click(); } }
    else if (a === 'confirm') {
      if (this.row === 2 && this.btn === 1) { this.host.show('charsel'); return; }
      this.go(); return;
    }
    this.render();
  }
}
